//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { Auth } from 'aws-amplify';

import { getQuerySeparator } from '@utils/getQuerySeparator';
import { CuralateAuthTokenHeader } from '@utils/sessionFetch';

import {
  IBaseUrls,
  IBasicCuralateSession,
  ICuralateSession,
} from 'app/basic-session/ICuralateSession';
import { crl8, isAuthedSession } from 'app/curalate.globals';
import logger from 'app/logger/logger';

export const sessionXhr = async (
  service: keyof IBaseUrls,
  endpoint: string,
  uploadProgress: (xhr: XMLHttpRequest, progress: number) => void,
  init: RequestInit,
): Promise<XMLHttpRequest> => {
  const url = `${crl8.session?.baseUrls[service]}${endpoint}`;
  const session = crl8.session as
    | ICuralateSession
    | IBasicCuralateSession
    | undefined;

  const headers: Record<string, string> =
    (init.headers && (init.headers as Record<string, string>)) ?? {};

  if (!init.credentials) {
    init.credentials = 'same-origin';
  }

  try {
    headers[CuralateAuthTokenHeader] = (await Auth.currentSession())
      .getIdToken()
      .getJwtToken();
  } catch (e) {
    logger.console.debug(`Couldn't build jwt header for request to ${url}`, e);
  }

  const safeClientId =
    (isAuthedSession(session) &&
      `${getQuerySeparator(url)}clientId=${session.client.id}`) ||
    '';

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(init.method ?? 'POST', `${url}${safeClientId}`);

    for (const key in headers) {
      const value = headers[key];

      if (value) {
        xhr.setRequestHeader(key, value);
      }
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        uploadProgress(xhr, event.loaded / event.total);
      }
    });

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        // Mark as a successful response if the status code is 2xx
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr);
        } else {
          reject(xhr);
        }
      }
    };
    xhr.send(init.body);
  });
};
